/* eslint-disable @typescript-eslint/no-unused-vars */
import { AdditionalActivatedChallengeFields, ChallengeLocation, ChallengeLocationSummary, ChartCore, ChartNamespace, CheckIn, GetPointResponse, Location, RegistrationField } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';


import { StateController } from '../helpers/state/state-management/state-controller';
import { areLocationsSame } from '../helpers/filters';
import challengeData from '../helpers/state/challenge-data';
import { escapeCsvValue } from '../helpers/files';
import { initCharts } from '../helpers/chart';
import { isDefined } from '../helpers/utils';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

declare const Chart: ChartNamespace;

@customElement('challenge-locations-progress')
export class ChallengeLocationsProgress extends LitElement {

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindChallengeState = new StateController(this, challengeData);



    @query('#myCheckinChart')
    myCheckinChart!: HTMLCanvasElement;

    @query('#myLocationChart')
    myLocationChart!: HTMLCanvasElement;

    @query('#myCategoryChart')
    myCategoryChart!: HTMLCanvasElement;

    @state()
    selectedLocation: string = 'all';

    @state()
    chart?: ChartCore;

    @state()
    locationChart?: ChartCore;

    @state()
    categoryChart?: ChartCore;

    @state()
    selectedSplitType: string = 'category';

    @state()
    checkinsByLocation: Array<{ChallengeLocation: ChallengeLocationSummary, count: number}> = [];

    async firstUpdated() {
        void initCharts().then((success) => {
            if (success) {
                void this.startup();
                void this.createCheckinChart();
                this.createLocationChart();
                this.createCategoryChart();
            }   else {
                console.error('Failed to load Chart.js');
            }
        }
        );
    }

    private async startup() {
        challengeData.bindChallengeLocationsToPoints(pointCache.allPoints);
        this.checkinsByLocation = this.setCheckInsByLocation();
    }

    async onFilterToLocation(e: CustomEvent) {
        this.selectedLocation = (e.target as HTMLInputElement).value;
        await this.createCheckinChart();
    }

    async onFilterToCategory(e: CustomEvent) {
        this.selectedSplitType = (e.target as HTMLInputElement).value;
        this.createCategoryChart();
    }

    get chartData() {
        if (challengeData.challengeLocations.length === 0) {
            challengeData.bindChallengeLocationsToPoints(pointCache.allPoints);
        };
        const allCheckins = challengeData.activated_challenges.flatMap(challenge => challenge.checkins);
        const location = challengeData.challengeLocations.find(location => location.point_info?._id.$oid === this.selectedLocation);
        const checkins = this.selectedLocation === 'all' ? allCheckins : allCheckins.filter(checkin => areLocationsSame(checkin?.location, location?.point_info?.location));
        // get unique days
        const uniqueDays = checkins
            .filter((checkin): checkin is CheckIn & { timestamp: string } => checkin?.timestamp !== undefined) // Type guard to filter out undefined timestamps
            .map(checkin => new Date(checkin.timestamp).toLocaleDateString()) // No need for optional chaining
            .filter((item, index, self) => index === self.findIndex(obj => obj === item))
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        const labels = uniqueDays.map(day => new Date(day));

        const datapoints = labels.map(label =>
            checkins.filter(checkin => checkin?.timestamp && new Date(checkin.timestamp).toLocaleDateString() === label.toLocaleDateString()).length
        );
        return { labels, datapoints };
    }

    setCheckInsByLocation() : Array<{ChallengeLocation: ChallengeLocationSummary, count: number}> {
        const checkins_by_location = challengeData.challengeLocations.map(location => {
            const checkins = challengeData.activated_challenges.flatMap(challenge => challenge.checkins);
            return {
                ChallengeLocation: location,
                count: checkins.filter(checkin => areLocationsSame(checkin?.location, location?.challenge_location_info?.location)).length
            };
        } );
        return checkins_by_location.sort((a, b) => b.count - a.count);
    }

    async createCheckinChart() {
        const { labels, datapoints } = this.chartData;
        const color = topicData.accentColor;

        if (isDefined(this.chart)) {
            // push new data
            this.chart.data.labels = labels;
            this.chart.data.datasets[0].data = datapoints;
            this.chart.update();
            return;
        }

        this.chart = new (window as any).Chart(this.myCheckinChart, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    data: datapoints,
                    label: 'Check-ins',
                    borderColor: color,
                    backgroundColor: color,
                }]
            },
            options: {
                animations: {
                    radius: {
                        duration: 400,
                        easing: 'linear',
                        loop: (context: { active: any; }) => context.active
                    }
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: false,
                    },
                    legend: {
                        display: false
                    },
                },
                interaction: {
                    intersect: false,
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: 'day',
                            displayFormats: {
                                day: 'MMM d',
                            }
                        },
                        title: {
                            display: true,
                            text: 'Date'
                        }
                    },
                    y: {
                        display: true,
                        title: {
                            display: true,
                            text: 'Number of Check-ins',
                        },
                    }
                }
            }
        });

    }

    createLocationChart() {
        if (isDefined(this.locationChart)) {
            this.locationChart.destroy();
        }
        const color = topicData.accentColor;
        const l_labels = this.checkinsByLocation.map(location => location.ChallengeLocation.point_info?.name ?? location.ChallengeLocation.challenge_location_info?.location?.search ?? 'Location');
        const l_datapoints = this.checkinsByLocation.map(location => location.count);

        // get first 30 characters of location name, if it's longer than 15 characters add '...'
        for (let i = 0; i < l_labels.length; i++) {
            if (l_labels[i].length > 30) {
                l_labels[i] = l_labels[i].substring(0, 30) + '...';
            }
        }

        this.locationChart = new (window as any).Chart(this.myLocationChart, {
            type: 'bar',
            data: {
                labels: l_labels,
                datasets: [{
                    data: l_datapoints,
                    label: 'Check-ins',
                    borderColor: color,
                    backgroundColor: color,
                }]
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                plugins: {
                    title: {
                        display: false,
                    },
                    legend: {
                        display: false
                    },
                },
                interaction: {
                    intersect: false,
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Location'
                        }
                    },
                    y: {
                        display: true,
                        title: {
                            display: true,
                            text: 'Locations',
                        },
                    }
                }
            }
        });
    }

    createCategoryChart() {
        if (isDefined(this.categoryChart)) {
            this.categoryChart.destroy();
        }
        const color = topicData.accentColor;
        const c_labels = [];
        const c_datapoints = [];
        if (this.selectedSplitType === 'category') {
            for (const category of topicData.categories) {
                const checkins = this.checkinsByLocation.filter(location => location.ChallengeLocation.point_info?.topic_category_id.$oid === category?._id?.$oid);
                c_labels.push(category.name);
                c_datapoints.push(checkins.reduce((acc, curr) => acc + curr.count, 0));
            }
        }
        else {
            for (const tag of topicData.allUserTags) {
                const checkins = this.checkinsByLocation.filter(location => location.ChallengeLocation.point_info?.tag_info?.user_tags.some(userTag => userTag.name === tag.name));
                c_labels.push(tag.name);
                c_datapoints.push(checkins.reduce((acc, curr) => acc + curr.count, 0));
            }
        }

        this.categoryChart = new (window as any).Chart(this.myCategoryChart, {
            type: 'bar',
            data: {
                labels: c_labels,
                datasets: [{
                    data: c_datapoints,
                    label: 'Check-ins',
                    borderColor: color,
                    backgroundColor: color,
                }]
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                plugins: {
                    title: {
                        display: false,
                    },
                    legend: {
                        display: false
                    },
                },
                interaction: {
                    intersect: false,
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: this.selectedSplitType === 'category' ? 'Category' : 'Tag'
                        }
                    },
                    y: {
                        display: true,
                        title: {
                            display: true,
                            text: 'Check-ins',
                        },
                    }
                }
            }
        });
    }



    private async handleClickDownloadAllCheckins() {
        // get latest checkins
        await challengeData.refreshActivatedChallenges();

        let content = '';
        const checkins = challengeData.activated_challenges.map(challenge => {
            const checkins = challenge.checkins ?? [];
            return checkins.map(checkin => {
                const object: ExportableCheckin = {
                    username: challenge.display_name ?? '',
                    email: challenge.email ?? '',
                    timestamp: checkin.timestamp ?? '',
                    point: challengeData.challengeLocations.find(location => areLocationsSame(location.challenge_location_info?.location, checkin.location))?.point_info as GetPointResponse,
                    phone: challenge.phone_number ?? '',
                    sign_up_date: challenge.created_at,
                    counted: challenge.counted_checkins?.some(countedCheckin => countedCheckin._id.$oid === checkin._id?.$oid) ?? false,
                    earned_rewards: challenge.earned_rewards.map(reward => reward.title).join(', '),
                };
                const additional_fields = challengeData.challenge?.registration_settings?.additional_fields ?? [];
                additional_fields.forEach(field => {
                    const labelWithoutSpaces = field.label.replace(/\s/g, '_');
                    object[labelWithoutSpaces] = challenge.additional_fields?.find(additionalField => additionalField.field_name === field.label)?.field_value;
                });

                return object;
            });
        }).flat();

        content +=
            `"Username","Email","Phone","Sign Up Date","Check-in Date","Counted Toward Total","Point Name","Point Address","Point Category","Point Tag","Earned Rewards"`;
        const additional_fields = challengeData.challenge?.registration_settings?.additional_fields ?? [];

        additional_fields.forEach(field => {
            content += `,"${field.label}"`;
        });
        content += '\n';

        checkins.forEach(checkin => {
            const category_name = topicData.categories.find(category => category._id?.$oid === checkin.point?.topic_category_id.$oid)?.name;
            content += `${escapeCsvValue(checkin.username)},${escapeCsvValue(checkin.email)},${escapeCsvValue(checkin.phone)},${escapeCsvValue(checkin.sign_up_date)},${escapeCsvValue(checkin.timestamp)},${checkin.counted=== true ? 'Yes' : 'No'},${escapeCsvValue(checkin.point?.name)},${escapeCsvValue(checkin.point?.location.search)},${escapeCsvValue(category_name ?? '')},${escapeCsvValue(checkin.point?.tag_info?.user_tags.map(tag => tag.name).join(', '))},${escapeCsvValue(checkin.earned_rewards)}`;
            additional_fields.forEach(field => {
                const labelWithoutSpaces = field.label.replace(/\s/g, '_');
                content += `,${escapeCsvValue(checkin[labelWithoutSpaces])}`;
            });
            content += '\n';
        });

        const blob = new Blob([content], { type: 'text/csv' });

        this.saveAs(blob, `checkins_${new Date().toISOString().split('T')[0]}.csv`);
    }


    saveAs(blob: Blob, fileName: string) {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    render() {
        return html`
            <div class="charts">
                <settings-card header="Check Ins by Date" .maxWidth=${'100%'}>
                    <div class="modalContent">
                        <div class="modal__content">
                            <p style="text-align: center;width: 100%;">
                                Check-Ins For
                                <proxi-selectfield-inline
                                    id="submission_type"
                                    outlined
                                    class="input reward-criteria"
                                    @selected=${this.onFilterToLocation}
                                    value="all"
                                >
                                    <mwc-list-item value="all">All Locations (default)</mwc-list-item>
                                        ${challengeData.challengeLocations?.map((location, index) => {
                                            const locationInfo = location.challenge_location_info?.location;
                                            const points = pointCache.getPointsFromLocation(locationInfo) ?? [];
                                            const searchText = locationInfo?.search ?? '';
                                            const pointName = points[0]?.name ?? '';

                                            return html`
                                                <mwc-list-item value="${points[0]?._id?.$oid ?? ''}">
                                                    ${points.length > 1
                                                        ? `${searchText} (${points.length})`
                                                        : `${pointName || searchText}`
                                                    }
                                                </mwc-list-item>
                                            `;
                                        })}
                                </proxi-selectfield-inline>
                            </p>
                            <div class="chart-container" style="position: relative; height:80%">
                                <canvas id="myCheckinChart" style="width:100%;"></canvas>
                            </div>
                        </div>
                    </div>
                    <div slot="header">
                        <form-button
                            size="md"
                            variant="outline"
                            class="action-button aqua-blue"
                            @click="${async () => await this.handleClickDownloadAllCheckins()}"
                        >
                            Download All Check-in Data
                        </form-button>
                    </div>
                </settings-card>
                <settings-card header="Check In Stats by Category or Tag" .maxWidth=${'100%'}>
                    <div class="modalContent">
                        <div class="modal__content">
                            <p style="text-align: center;width: 100%;">
                                Check-Ins By
                                <proxi-selectfield-inline
                                    id="submission_type"
                                    outlined
                                    class="input reward-criteria"
                                    @selected=${this.onFilterToCategory}
                                    value="category"
                                >
                                    <mwc-list-item value="category">Category</mwc-list-item>
                                    <mwc-list-item value="tag">Tag</mwc-list-item>
                                </proxi-selectfield-inline>
                            </p>
                            <div class="chart-container" style="position: relative; height:80%">
                                <canvas id="myCategoryChart" style="width:100%;"></canvas>
                            </div>
                        </div>
                    </div>
                </settings-card>
            </div>
            <settings-card header="Check In Stats by Location" .maxWidth=${'100%'}>
            <div class="modal__content">
                    <div class="challenges-wrapper">
                        <canvas id="myLocationChart" height="${this.checkinsByLocation.length * 12}"></canvas>
                    </div>
                </div>
            </settings-card>
        `;
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
            }

            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: var(--theme-input-color);
                font-size: 18px;
                font-weight: 700;
                line-height: 1.3em;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
                flex: 1;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
            }


            .modal__content::-webkit-scrollbar {
                width: 5px;
            }
            /* Track */
            .modal__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }
            /* Handle */
            .modal__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .charts {
                display: flex;
                flex-direction: row;
                gap: 10px;
                margin-bottom: 20px;
            }

            #myCheckinChart {
                min-height: 300px;
            }

            settings-card {
                flex: 1;
            }

            .modal__footer {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding: 5px 0px;
                box-sizing: border-box;
                gap: 10px;
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row,
            .challenge-row__header {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-around;
                padding: 4px 0;
                box-sizing: border-box;
                gap: 10px;
            }

            .challenge-row__header {
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--main-primary-color);
                text-align: center;
            }

            .participant {
                width: 10%;
                box-sizing: border-box;
                font-size: 12px;
                line-height: 120%;
                color: #1D1B20;
                display: flex;
                justify-content: center;
            }

            .point {
                width: 40%;
                box-sizing: border-box;
                line-height: 24px;
                color: #1D1B20;
                flex: 1;
                text-align: left;
            }

            .checkin-value {
                width: 10%;
                box-sizing: border-box;
                font-size: 12px;
                line-height: 120%;
                color: #1D1B20;
            }

            .confirm-code {
                width: 10%;
                box-sizing: border-box;
                font-size: 12px;
                line-height: 120%;
                color: #1D1B20;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .hidden {
                display: none;
            }

            .input__checkin-value {
                width: 46px;
            }

            .input__confirm-code {
                width: 130px;
            }


            .category-name {
                font-size: 12px;
                line-height: 120%;
                color: #49454F;
            }

            .place-name {
                font-size: 16px;
                line-height: 150%;
                color: #1D1B20;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}

interface ExportableCheckin {
    username: string;
    email: string;
    phone: string;
    sign_up_date: string;
    timestamp: string;
    point?: GetPointResponse;
    earned_rewards: string;
    [key: string]: any; // Index signature for additional fields
}